import { isImpersonating } from './impersonation'
import type { ActiveTourRecord, Tour, TourProgressEntry, TourStatus } from '../types'

/**
 * Per-browser tour progress. Mirrors the `nxtgen.queue.v1.<userId>` pattern:
 * keyed by user id so a second person on the same browser starts clean, and
 * every access is try/catch (private mode, quota, disabled storage).
 * Not cross-device — see docs/features/product-walkthrough.md.
 */
const progressKey = (userId: string) => `nxtgen.tour.v1.${userId}`
const activeKey = (userId: string) => `nxtgen.tour.v1.active.${userId}`
const autoAdvanceKey = (userId: string) => `nxtgen.tour.v1.autoadvance.${userId}`

/** A refresh resumes the tour only within this window. */
export const RESUME_TTL_MS = 30 * 60 * 1000

type ProgressMap = Record<string, TourProgressEntry>

export function loadProgress(userId: string): ProgressMap {
  try {
    const raw = localStorage.getItem(progressKey(userId))
    const parsed: unknown = raw ? JSON.parse(raw) : {}
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as ProgressMap) : {}
  } catch {
    return {}
  }
}

/** A stored entry for an OLDER version counts as not started — that is how a bump re-offers a tour. */
export function getTourStatus(userId: string, tour: Pick<Tour, 'id' | 'version'>): TourStatus {
  const entry = loadProgress(userId)[tour.id]
  if (entry?.version !== tour.version) return 'not_started'
  return entry.status
}

export function setTourStatus(
  userId: string,
  tour: Pick<Tour, 'id' | 'version'>,
  status: TourProgressEntry['status'],
  now = new Date(),
): void {
  if (isImpersonating()) return // never on the impersonator's browser
  try {
    const map = loadProgress(userId)
    map[tour.id] = { version: tour.version, status, at: now.toISOString() }
    localStorage.setItem(progressKey(userId), JSON.stringify(map))
  } catch {
    /* storage unavailable — progress is a convenience, never block the tour */
  }
}

/** The user declined a tour without starting it (distinct from `skipped` mid-tour). */
export function markTourDismissed(userId: string, tour: Pick<Tour, 'id' | 'version'>): void {
  setTourStatus(userId, tour, 'dismissed')
}

export function saveActive(userId: string, record: ActiveTourRecord): void {
  if (isImpersonating()) return // never on the impersonator's browser
  try {
    sessionStorage.setItem(activeKey(userId), JSON.stringify(record))
  } catch {
    /* ignore */
  }
}

export function loadActive(userId: string, now = Date.now()): ActiveTourRecord | null {
  try {
    const raw = sessionStorage.getItem(activeKey(userId))
    if (!raw) return null
    const rec = JSON.parse(raw) as Partial<ActiveTourRecord>
    if (
      typeof rec.tourId !== 'string' ||
      typeof rec.stepId !== 'string' ||
      typeof rec.path !== 'string' ||
      typeof rec.version !== 'number' ||
      typeof rec.savedAt !== 'number' ||
      now - rec.savedAt > RESUME_TTL_MS ||
      rec.savedAt > now
    ) {
      clearActive(userId)
      return null
    }
    const source = rec.source === 'menu' || rec.source === 'help' || rec.source === 'welcome' || rec.source === 'invite' ? rec.source : 'api'
    return { ...(rec as ActiveTourRecord), source }
  } catch {
    return null
  }
}

export function clearActive(userId: string): void {
  try {
    sessionStorage.removeItem(activeKey(userId))
  } catch {
    /* ignore */
  }
}

/** The user pressed Pause on a tour card's auto-advance (remembered per user, per browser). */
export function getAutoAdvancePaused(userId: string): boolean {
  try {
    return localStorage.getItem(autoAdvanceKey(userId)) === 'paused'
  } catch {
    return false
  }
}

export function setAutoAdvancePaused(userId: string, paused: boolean): void {
  if (isImpersonating()) return // never on the impersonator's browser
  try {
    if (paused) localStorage.setItem(autoAdvanceKey(userId), 'paused')
    else localStorage.removeItem(autoAdvanceKey(userId))
  } catch {
    /* ignore */
  }
}
